import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { CaretLeft, CaretRight, Calendar as CalendarIcon, ListChecks, Leaf, Briefcase, ArrowSquareOut } from "@phosphor-icons/react";
import { formatDate, formatTime } from "../lib/format";

const WEEKDAYS = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];

const KIND_META = {
  meeting:       { label: "Reunión",        icon: CalendarIcon, color: "#1D4ED8", route: "/reuniones" },
  commitment:    { label: "Compromiso",     icon: ListChecks,   color: "#D97706", route: "/compromisos" },
  rca:           { label: "RCA",            icon: Leaf,         color: "#0F766E", route: "/rca" },
  project_start: { label: "Inicio proyecto", icon: Briefcase,   color: "#7C3AED", route: "/proyectos" },
  project_end:   { label: "Cierre proyecto", icon: Briefcase,   color: "#9333EA", route: "/proyectos" },
};

function pad(n) { return String(n).padStart(2, "0"); }
function iso(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function buildMonthGrid(year, month) {
  // month is 0-indexed
  const first = new Date(year, month, 1);
  // weekday: 0=Sun ... 6=Sat. We want Monday start: offset
  const offset = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < offset; i++) {
    const d = new Date(year, month, -offset + i + 1);
    days.push({ date: d, currentMonth: false });
  }
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= lastDay; d++) {
    days.push({ date: new Date(year, month, d), currentMonth: true });
  }
  // Fill to multiple of 7
  while (days.length % 7 !== 0) {
    const last = days[days.length - 1].date;
    const next = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
    days.push({ date: next, currentMonth: false });
  }
  return days;
}

const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export default function Agenda() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ meeting: true, commitment: true, rca: true, project_start: true, project_end: true });
  const [selectedDay, setSelectedDay] = useState(null);

  const days = useMemo(() => buildMonthGrid(year, month), [year, month]);

  useEffect(() => {
    const first = days[0].date;
    const last = days[days.length - 1].date;
    api.get("/agenda", { params: { from: iso(first), to: iso(last) } })
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]));
  }, [days]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach(e => {
      if (!filters[e.kind]) return;
      const key = (e.date || "").slice(0, 10);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [events, filters]);

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1);
  };
  const goToday = () => {
    setYear(today.getFullYear()); setMonth(today.getMonth());
  };

  const todayKey = iso(today);
  const selectedKey = selectedDay ? iso(selectedDay) : null;
  const selectedEvents = selectedKey ? (eventsByDay[selectedKey] || []) : [];

  return (
    <div data-testid="agenda-page">
      <PageHeader
        title="Agenda y calendario"
        description="Vista consolidada de asuntos públicos: reuniones, compromisos, RCA y hitos de proyectos. Click en un día para ver el detalle."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={goPrev} data-testid="cal-prev"><CaretLeft /></Button>
            <Button variant="outline" onClick={goToday} data-testid="cal-today">Hoy</Button>
            <Button variant="outline" onClick={goNext} data-testid="cal-next"><CaretRight /></Button>
            <div className="font-display text-xl font-bold ml-2 min-w-[180px]">{MONTH_NAMES[month]} {year}</div>
          </div>
        }
      />

      <div className="px-6 py-6 space-y-4">
        {/* Filters legend */}
        <Card className="rounded-md">
          <CardContent className="p-3 flex flex-wrap gap-2 items-center text-xs">
            <span className="text-slate-500 mr-2">Mostrar:</span>
            {Object.entries(KIND_META).map(([k, m]) => (
              <button
                key={k}
                onClick={() => setFilters({ ...filters, [k]: !filters[k] })}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors ${
                  filters[k] ? "border-slate-300 bg-white text-slate-800" : "border-slate-200 bg-slate-50 text-slate-400 line-through"
                }`}
                data-testid={`filter-${k}`}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: m.color }} />
                {m.label}
              </button>
            ))}
            <span className="ml-auto text-slate-500">{events.filter(e => filters[e.kind]).length} eventos en {MONTH_NAMES[month]}</span>
          </CardContent>
        </Card>

        {/* Calendar grid */}
        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
            {WEEKDAYS.map(w => (
              <div key={w} className="px-2 py-2 text-[10px] uppercase tracking-[0.15em] text-slate-500 text-center font-medium">{w}</div>
            ))}
          </div>
          {/* Days */}
          <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(120px, 1fr)" }}>
            {days.map((d, idx) => {
              const key = iso(d.date);
              const isToday = key === todayKey;
              const dayEvents = eventsByDay[key] || [];
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDay(d.date)}
                  className={`text-left border-r border-b border-slate-100 last:border-r-0 p-2 hover:bg-slate-50 transition-colors flex flex-col gap-1 overflow-hidden ${d.currentMonth ? "" : "bg-slate-50/50"}`}
                  data-testid={`day-${key}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${isToday ? "h-6 w-6 inline-flex items-center justify-center rounded-full bg-slate-900 text-white" : d.currentMonth ? "text-slate-700" : "text-slate-400"}`}>
                      {d.date.getDate()}
                    </span>
                    {dayEvents.length > 0 && <span className="text-[10px] text-slate-500">{dayEvents.length}</span>}
                  </div>
                  <div className="flex-1 space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map((e, i) => (
                      <div key={i} className="text-[10px] leading-tight truncate rounded px-1.5 py-0.5 text-white" style={{ background: e.color }} title={e.title}>
                        {e.time ? `${formatTime(e.time)} ` : ""}{e.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-slate-500 px-1.5">+ {dayEvents.length - 3} más</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Day detail panel */}
      <Sheet open={!!selectedDay} onOpenChange={(o) => { if (!o) setSelectedDay(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="day-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">{selectedDay && formatDate(iso(selectedDay))}</SheetTitle>
            <div className="text-xs uppercase tracking-wider text-slate-500">{selectedEvents.length} eventos · Zona horaria Chile</div>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            {selectedEvents.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">Sin eventos este día.</div>
            ) : selectedEvents.map((e, i) => {
              const meta = KIND_META[e.kind];
              const detailRoute = e.kind === "meeting" ? `/reuniones/${e.id}` : meta.route;
              return (
                <Link key={i} to={detailRoute} className="block">
                  <div className="rounded-md border border-slate-200 bg-white p-3 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}1A`, color: meta.color }}>
                        <meta.icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{meta.label}</div>
                        <div className="font-medium text-sm">{e.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
                          {e.time && <span>{formatTime(e.time)}</span>}
                          {e.lugar && <span>· {e.lugar}</span>}
                          {e.tipo && <span>· {e.tipo}</span>}
                          {e.componente && <span>· {e.componente}</span>}
                          {e.responsable && <span>· {e.responsable}</span>}
                          {e.responsable_interno && <span>· {e.responsable_interno}</span>}
                        </div>
                      </div>
                      <ArrowSquareOut size={14} className="text-slate-400" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
